import React from 'react';
import { alpha, Box, Container, Skeleton } from '@mui/material';

const sk = {
  bgcolor: '#e8edf3',
  transform: 'none',
  '&::after': { animationDuration: '1.1s' },
};

function Sk({ variant = 'text', width, height = 12, sx = {} }) {
  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation="wave"
      sx={{ ...sk, borderRadius: variant === 'circular' ? '50%' : variant === 'rounded' ? 1 : 0.5, ...sx }}
    />
  );
}

/** Stats grid skeleton */
export function PerformanceMetricsSkeleton() {
  return (
    <Box sx={{ borderRadius: 1, border: '1px solid #e5e7eb', bgcolor: '#fff', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.25, py: 0.6 }}>
        <Sk width={100} height={10} />
        <Sk width={72} height={10} />
      </Box>
      <Box sx={{ display: 'flex', borderTop: '1px solid #eef2f7' }}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ flex: 1, px: 1.25, py: 0.7, borderRight: i < 4 ? '1px solid #eef2f7' : 'none' }}>
            <Sk width={48} height={8} sx={{ mb: 0.35 }} />
            <Sk width={40} height={14} />
          </Box>
        ))}
      </Box>
      <Box sx={{ px: 1.25, py: 0.55, borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
        <Sk width="85%" height={10} />
      </Box>
    </Box>
  );
}

/** Capitulation grid table skeleton (7 columns) */
export function CapitulationTableSkeleton({ rows = 8 }) {
  const cols = '1fr 0.8fr 1fr 0.8fr 1fr 1fr 0.8fr';
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Sk width={140} height={14} />
        <Sk width={200} height={11} />
      </Box>
      <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: cols,
          p: '8px 12px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '1px solid #e2e8f0',
        }}>
          {[52, 44, 48, 36, 56, 64, 40].map((w, i) => (
            <Sk key={i} width={w} height={10} sx={{ justifySelf: i >= 2 && i <= 5 ? 'end' : i === 6 ? 'center' : 'start' }} />
          ))}
        </Box>
        {Array.from({ length: rows }).map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'grid',
              gridTemplateColumns: cols,
              p: '7px 12px',
              borderBottom: '1px solid #f8fafc',
              borderLeft: `3px solid ${alpha('#ef4444', idx % 3 === 0 ? 0.5 : 0.2)}`,
              bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
            }}
          >
            <Sk width={idx % 2 === 0 ? 44 : 52} height={14} sx={{ bgcolor: alpha('#2563eb', 0.08) }} />
            <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
              {[0, 1, 2].map((d) => (
                <Sk key={d} variant="circular" width={7} height={7} />
              ))}
              <Sk width={28} height={10} />
            </Box>
            <Sk width={56} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={36} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={64} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={48} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={44} height={20} sx={{ justifySelf: 'center', borderRadius: 99 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Snapback grid table skeleton (10 columns) */
export function SnapbackTableSkeleton({ rows = 6 }) {
  const cols = '0.9fr 0.7fr 0.9fr 1.2fr 0.8fr 0.8fr 0.9fr 0.9fr 0.7fr 1fr';
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Sk width={120} height={14} />
        <Sk width={180} height={11} />
      </Box>
      <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: cols,
          p: '8px 12px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '1px solid #e2e8f0',
        }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Sk key={i} width={i === 3 ? 48 : 36} height={10} />
          ))}
        </Box>
        {Array.from({ length: rows }).map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'grid',
              gridTemplateColumns: cols,
              p: '7px 12px',
              borderBottom: '1px solid #f8fafc',
              borderLeft: `3px solid ${alpha('#16a34a', 0.35)}`,
              bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
            }}
          >
            <Sk width={48} height={14} />
            <Sk width={32} height={14} sx={{ justifySelf: 'center' }} />
            <Sk width={52} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={72} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={48} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={48} height={14} sx={{ justifySelf: 'end' }} />
            <Sk width={56} height={14} sx={{ justifySelf: 'center' }} />
            <Sk width={40} height={14} sx={{ justifySelf: 'center' }} />
            <Sk width={28} height={14} sx={{ justifySelf: 'center' }} />
            <Sk width={64} height={24} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TabsSkeleton() {
  return (
    <Box sx={{ borderBottom: '2px solid #f1f5f9', mb: 2, mt: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 2, minHeight: 40, alignItems: 'center', px: 0.5 }}>
        {[88, 76, 88, 72].map((w, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
            <Sk variant="circular" width={18} height={18} />
            <Sk width={w} height={14} />
            {i < 3 && <Sk width={22} height={16} sx={{ borderRadius: 99 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Full page skeleton — matches ReversalCatcherScanner layout */
const ReversalCatcherSkeleton = ({ activeTab = 'snapback' }) => {
  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Sk variant="rounded" width={44} height={44} sx={{ borderRadius: '12px', flexShrink: 0 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
              <Sk width={200} height={22} />
              <Sk width={88} height={22} sx={{ borderRadius: 1.5 }} />
            </Box>
            <Sk width={320} height={12} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Sk variant="rounded" width={88} height={32} />
          <Sk variant="rounded" width={88} height={32} />
          <Sk variant="rounded" width={100} height={32} />
        </Box>
      </Box>

      <PerformanceMetricsSkeleton />
      <TabsSkeleton />

      {activeTab === 'snapback' ? (
        <SnapbackTableSkeleton rows={6} />
      ) : (
        <CapitulationTableSkeleton rows={8} />
      )}
    </Container>
  );
};

export default ReversalCatcherSkeleton;
